import { useState, useEffect } from "react";
import { FileText, Shield, Database, ChevronDown, ChevronUp, Check, AlertTriangle } from "lucide-react";

/**
 * Composant de consentement pour la première connexion
 * Affiche les CGU, Politique de confidentialité et demande les consentements
 */
export default function ConsentModal({ 
  isOpen, 
  userRole, // "PATIENT" | "IDE" | "MEDECIN" | "SECRETAIRE_MEDICAL" | etc.
  onAccept,
  loading = false
}) {
  const [expandedSection, setExpandedSection] = useState(null);
  const [consents, setConsents] = useState({
    cgu: false,
    privacy: false,
    dataResearch: false,
    dataPharma: false,
  });

  if (!isOpen) return null;

  const isPatient = userRole === "PATIENT";
  const cndpMention = "SARL AU KIAAD, sise 15 avenue Al Abtal, Rabat Agdal, 10000 Rabat, RC 187377, ICE 003737330000026, représentée par Marouan Zouirhri, collecte vos données personnelles en vue du suivi et de la mise en relation des patients avec les professionnels de santé référents. Ce traitement fait l'objet d'une autorisation préalable de la CNDP n° A-S-1164/2026. Pour exercer vos droits d'accès, de rectification et d'opposition conformément aux dispositions de la loi 09-08, vous pouvez contacter SARL AU KIAAD à l'adresse contact@kiaad.ma ou au 0716204810.";
  const proRegulation = "Le professionnel de santé déclare être autorisé à exercer sa profession conformément à la réglementation en vigueur, notamment au regard des règles d'habilitation, d'inscription ou d'autorisation applicables auprès de l'ordre ou de l'autorité professionnelle compétente.";
  
  // Vérifier si les consentements obligatoires sont cochés
  const canProceed = consents.cgu && consents.privacy;

  const handleAccept = () => {
    if (canProceed) {
      onAccept({
        cguAccepted: true,
        cguAcceptedAt: new Date().toISOString(),
        cguVersion: "cndp-A-S-1164-2026",
        privacyAccepted: true,
        privacyAcceptedAt: new Date().toISOString(),
        privacyVersion: "cndp-A-S-1164-2026",
        dataResearchConsent: consents.dataResearch,
        dataResearchConsentAt: consents.dataResearch ? new Date().toISOString() : null,
        dataPharmaConsent: consents.dataPharma,
        dataPharmaConsentAt: consents.dataPharma ? new Date().toISOString() : null,
      });
    }
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 bg-gradient-to-r from-teal-600 to-emerald-600 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Bienvenue sur HANA</h2>
              <p className="text-teal-100 text-sm">
                Avant de continuer, veuillez prendre connaissance des documents suivants
              </p>
            </div>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          
          {/* Avertissement urgence pour patients */}
          {isPatient && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-red-800">En cas d'urgence médicale</p>
                <p className="text-sm text-red-700 mt-1">
                  Cette application n'est <strong>PAS</strong> un service d'urgence. 
                  En cas d'urgence, appelez immédiatement le <strong>141 (SAMU)</strong> ou 
                  rendez-vous aux urgences les plus proches.
                </p>
              </div>
            </div>
          )}

          {/* Section CGU */}
          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <button
              onClick={() => toggleSection("cgu")}
              data-testid="consent-cgu-section-toggle"
              className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-slate-600" />
                <span className="font-medium text-slate-800">Conditions Générales d'Utilisation</span>
              </div>
              {expandedSection === "cgu" ? (
                <ChevronUp className="w-5 h-5 text-slate-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-slate-400" />
              )}
            </button>
            {expandedSection === "cgu" && (
              <div className="p-4 bg-white border-t border-slate-200 max-h-64 overflow-y-auto text-sm text-slate-600 space-y-3">
                <h4 className="font-semibold text-slate-800">Article 1 - Éditeur et responsabilités</h4>
                <p>{cndpMention}</p>
                {!isPatient && <p>{proRegulation}</p>}

                <h4 className="font-semibold text-slate-800">Article 2 - Description du service</h4>
                {isPatient ? (
                  <ul className="list-disc list-inside space-y-1">
                    <li>Declaration quotidienne des symptomes (13 items CTCAE) et constantes vitales</li>
                    <li>Réception de notifications In-App et PWA liées au suivi</li>
                    <li>Consultation de votre historique medical</li>
                    <li>Communication securisee avec votre equipe soignante</li>
                    <li>Module d'education therapeutique</li>
                  </ul>
                ) : (
                  <ul className="list-disc list-inside space-y-1">
                    <li>Suivi des patients assignes et gestion des alertes cliniques</li>
                    <li>Evaluations cliniques structurees (28 types de cancer)</li>
                    <li>Resultats biologiques (22 parametres)</li>
                    <li>Communication securisee et prescriptions</li>
                  </ul>
                )}
                
                <h4 className="font-semibold text-slate-800">Article 3 - Limites du service</h4>
                <p className="text-red-600 font-medium">
                  Cette plateforme N'EST PAS un service d'urgence. En cas d'urgence medicale, appelez le 141 (SAMU) ou rendez-vous aux urgences.
                </p>
                
                <h4 className="font-semibold text-slate-800">Article 4 - Données et accès</h4>
                <p>Les données sont traitées pour le suivi et la mise en relation avec les professionnels de santé référents. Les accès sont limités aux personnes autorisées selon leur rôle. Les données RWE ne sont utilisées séparément que sous pseudonymisation forte avec techniques d’anonymisation et consentement spécifique.</p>
                
                <h4 className="font-semibold text-slate-800">Article 5 - Obligations de l'utilisateur</h4>
                <ul className="list-disc list-inside space-y-1">
                  <li>Fournir des informations exactes et a jour</li>
                  <li>Maintenir la confidentialite de vos identifiants</li>
                  <li>Signaler immediatement tout usage non autorise</li>
                  {!isPatient && <li>Ne consulter que les dossiers des patients dont vous avez la charge</li>}
                </ul>
                
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <a href="/cndp-ready/14_CGU_HANA.pdf" target="_blank" rel="noopener noreferrer"
                    data-testid="consent-cgu-pdf-link"
                    className="text-teal-600 hover:underline text-xs font-medium">
                    Telecharger le document complet (PDF)
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Section Politique de confidentialité */}
          <div className="border border-slate-200 rounded-xl overflow-hidden">
            <button
              onClick={() => toggleSection("privacy")}
              data-testid="consent-privacy-section-toggle"
              className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-slate-600" />
                <span className="font-medium text-slate-800">Politique de Confidentialité</span>
              </div>
              {expandedSection === "privacy" ? (
                <ChevronUp className="w-5 h-5 text-slate-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-slate-400" />
              )}
            </button>
            {expandedSection === "privacy" && (
              <div className="p-4 bg-white border-t border-slate-200 max-h-64 overflow-y-auto text-sm text-slate-600 space-y-3">
                <h4 className="font-semibold text-slate-800">Responsabilités</h4>
                <p>{cndpMention}</p>

                <h4 className="font-semibold text-slate-800">Base legale (Loi 09-08 / CNDP)</h4>
                <ul className="list-disc list-inside space-y-1">
                  <li><strong>Consentement explicite</strong> pour les donnees de sante</li>
                  <li><strong>Execution du contrat</strong> pour le service de telesuivi</li>
                  <li><strong>Obligation legale</strong> pour la conservation des donnees (10 ans min)</li>
                </ul>

                <h4 className="font-semibold text-slate-800">Donnees collectees</h4>
                <ul className="list-disc list-inside space-y-1">
                  <li>Donnees d'identification (nom, telephone, email)</li>
                  {isPatient && <li>Donnees de sante : 13 symptomes CTCAE, constantes vitales, biologie (22 parametres), questionnaires PRO</li>}
                  <li>Donnees techniques (IP, empreinte navigateur, journaux d'acces)</li>
                </ul>
                
                <h4 className="font-semibold text-slate-800">Duree de conservation</h4>
                <ul className="list-disc list-inside space-y-1">
                  <li>Donnees de sante : 10 ans apres derniere activite</li>
                  <li>Donnees professionnels : duree du contrat + 3 ans</li>
                  <li>Données RWE pseudonymisées : selon les contrats et consentements applicables</li>
                </ul>

                <h4 className="font-semibold text-slate-800">Vos droits (Art. 7-9, Loi 09-08)</h4>
                <p>Droit d'accès, de rectification et d'opposition conformément aux dispositions de la loi 09-08. Contact : contact@kiaad.ma ou 0716204810.</p>
                <p>Reclamation possible aupres de la CNDP (Commission Nationale de controle de la protection des Donnees).</p>
                
                <h4 className="font-semibold text-slate-800">Securite</h4>
                <p>Chiffrement TLS, authentification des utilisateurs, limitation des accès par rôle et clinique, pseudonymisation forte RWE et journalisation des actions sensibles.</p>
                
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <a href="/cndp-ready/06_POLITIQUE_CONFIDENTIALITE.pdf" target="_blank" rel="noopener noreferrer"
                    data-testid="consent-privacy-pdf-link"
                    className="text-teal-600 hover:underline text-xs font-medium">
                    Telecharger le document complet (PDF)
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Checkboxes de consentement */}
          <div className="space-y-3 pt-2">
            <p className="text-sm font-medium text-slate-700">Consentements requis :</p>
            
            {/* CGU - Obligatoire */}
            <label className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors">
              <input
                type="checkbox"
                data-testid="consent-cgu-checkbox"
                checked={consents.cgu}
                onChange={(e) => setConsents({ ...consents, cgu: e.target.checked })}
                className="mt-1 w-5 h-5 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
              />
              <div>
                <span className="text-sm font-medium text-slate-800">
                  J'accepte les Conditions Générales d'Utilisation *
                </span>
              </div>
            </label>

            {/* Politique de confidentialité - Obligatoire */}
            <label className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors">
              <input
                type="checkbox"
                data-testid="consent-privacy-checkbox"
                checked={consents.privacy}
                onChange={(e) => setConsents({ ...consents, privacy: e.target.checked })}
                className="mt-1 w-5 h-5 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
              />
              <div>
                <span className="text-sm font-medium text-slate-800">
                  J'ai lu et j'accepte la Politique de Confidentialité *
                </span>
              </div>
            </label>

            {/* Consentements recherche - Optionnels pour patients uniquement */}
            {isPatient && (
              <div className="space-y-3">
                <label className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors border border-blue-200">
                  <input
                    type="checkbox"
                    data-testid="consent-research-academic-checkbox"
                    checked={consents.dataResearch}
                    onChange={(e) => setConsents({ ...consents, dataResearch: e.target.checked })}
                    className="mt-1 w-5 h-5 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <Database className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-800">
                        Recherche médicale académique (optionnel)
                      </span>
                    </div>
                    <p className="text-xs text-blue-600 mt-1">
                      J'accepte que mes données structurées, sous pseudonymisation forte,
                      contribuent à des travaux médicaux ou scientifiques. Ce choix est facultatif
                      et peut être retiré à tout moment.
                    </p>
                  </div>
                </label>

                <label className="flex items-start gap-3 p-3 bg-emerald-50 rounded-lg cursor-pointer hover:bg-emerald-100 transition-colors border border-emerald-200">
                  <input
                    type="checkbox"
                    data-testid="consent-research-pharma-checkbox"
                    checked={consents.dataPharma}
                    onChange={(e) => setConsents({ ...consents, dataPharma: e.target.checked })}
                    className="mt-1 w-5 h-5 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm font-medium text-emerald-800">
                        Études RWE avec partenaires Pharma (optionnel)
                      </span>
                    </div>
                    <p className="text-xs text-emerald-700 mt-1">
                      J'accepte que mes données structurées soient utilisées dans des études de vie réelle,
                      sans nom, téléphone, documents bruts, photos brutes ni messages libres.
                    </p>
                  </div>
                </label>
              </div>
            )}

            <p className="text-xs text-slate-400">* Champs obligatoires</p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 bg-slate-50 border-t border-slate-200">
          <button
            onClick={handleAccept}
            disabled={!canProceed || loading}
            data-testid="consent-submit-button"
            className={`w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all ${
              canProceed
                ? "bg-teal-600 text-white hover:bg-teal-700"
                : "bg-slate-200 text-slate-400 cursor-not-allowed"
            }`}
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <Check className="w-5 h-5" />
                Accepter et continuer
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
